import { useUser } from '../contexts/UserContext';
import { useMsal } from '@azure/msal-react';
import { useSystemParameters } from '../hooks/useSystemParameters';
import { useState, useEffect } from 'react';

function UserGuide() {
  const { user } = useUser();
  const { accounts } = useMsal();
  const { getBooleanValue } = useSystemParameters();
  const [bypassTokenValidation, setBypassTokenValidation] = useState<boolean>(false);
  const [bypassUserInfo, setBypassUserInfo] = useState<any>(null);

  // Check for security bypass parameter
  useEffect(() => {
    const checkBypassParameter = async () => {
      const bypassValue = getBooleanValue('security.bypass', false);
      setBypassTokenValidation(bypassValue === true);
      
      if (bypassValue === true && accounts.length > 0) {
        setBypassUserInfo({
          roles: [{ name: 'Bypass User' }]
        });
      }
    };

    checkBypassParameter();
  }, [getBooleanValue, accounts]);

  const displayUser = bypassTokenValidation ? bypassUserInfo : user;
  const userRoles = displayUser?.roles || [];
  
  // Determine user role type
  const isAdmin = userRoles.some((r: any) => r.id === 'admin-role-id');
  const isAgencyAdmin = userRoles.some((r: any) => r.id === 'agency-admin-role-id');
  const isAgencyUser = userRoles.some((r: any) => r.id === 'agency-user-role-id');
  const isGroupUser = userRoles.some((r: any) => r.id === 'group-user-role-id');
  const isCustomer = userRoles.some((r: any) => r.id === 'customer-role-id');
  const isCustomerAdmin = userRoles.some((r: any) => r.id === 'customer-admin-role-id');
  const isBypass = userRoles.some((r: any) => r.name === 'Bypass User');

  // Get role name for display
  const getRoleName = () => {
    if (isAdmin) return 'Admin';
    if (isAgencyAdmin) return 'Agency Admin';
    if (isAgencyUser) return 'Agency User';
    if (isGroupUser) return 'Group User';
    if (isCustomerAdmin) return 'Customer Admin';
    if (isCustomer) return 'Customer';
    if (isBypass) return 'Bypass User';
    return 'User';
  };

  // Define features based on role
  const getFeatures = () => {
    const features = [];

    // Features available to all users
    const dashboardDescription = `
      <div style="margin-top: 16px;">
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; font-size: 1rem; font-weight: 600;">Overview</h4>
        <p style="margin-bottom: 16px; color: #495057; line-height: 1.6; font-size: 14px;">
          The Dashboard is your main workspace for viewing and managing transaction records. It provides quick access to transaction statistics, powerful search capabilities, and record management tools.
        </p>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Status Cards</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          At the top of the Dashboard, you'll see status cards showing:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li><strong>Total Card:</strong> Shows the total number of transactions matching your current search filters. Click it to view all transactions.</li>
          <li><strong>Status Cards:</strong> Each card displays the count of transactions for a specific status (e.g., "Completed", "Pending", "Processing"). Click any status card to filter transactions by that status.</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Search & Filter</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          Use the search section to find specific transactions:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li><strong>VIN:</strong> Enter a Vehicle Identification Number (minimum 8 characters). Press Enter or click Search to find matching records.</li>
          <li><strong>Client Number:</strong> Search by client number to find all transactions for a specific client.</li>
          <li><strong>Unit Number:</strong> Filter by unit number to locate specific vehicle transactions.</li>
          <li><strong>Requestor:</strong> Search by the person or department who requested the transaction.</li>
          ${isAdmin ? '<li><strong>Agency:</strong> (Admin only) Select an agency from the dropdown to filter transactions. Select "All Agencies" to view transactions across all agencies.</li>' : ''}
          <li><strong>Status:</strong> Filter by transaction status from the dropdown menu. Status options are configured per agency.</li>
        </ul>
        <p style="margin-bottom: 16px; color: #495057; line-height: 1.6; font-size: 14px;">
          You can combine multiple search criteria. Click the <strong>Search</strong> button or press <strong>Enter</strong> in any search field to execute the search.
        </p>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Transaction Table</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          The results table displays transaction records with the following features:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li><strong>Columns:</strong> VIN, Client Number, Unit Number, ETA (Estimated Completion Date), Status, Transaction Type, State, Vendor, and Requestor.</li>
          <li><strong>Change Status:</strong> Click the status dropdown in any row to quickly change the transaction status without opening the edit modal.</li>
          <li><strong>View Details:</strong> Click the eye icon to open a detailed view of the transaction with all fields including dates, tracking numbers, and notes.</li>
          <li><strong>Edit Record:</strong> Click the edit icon to modify transaction details. Required fields (VIN and Status) must be filled.</li>
          <li><strong>Pagination:</strong> Use the Previous/Next buttons and page selector to navigate through results. Adjust "Rows per page" to show 5, 10, 50, or 100 records per page.</li>
          <li><strong>Export:</strong> Click the <strong>Export</strong> button to download all matching transactions as an Excel file. The export includes all records matching your current search filters.</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Tips</h4>
        <ul style="margin-bottom: 0; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li>Status cards update automatically when you change search filters.</li>
          <li>Clicking a status card immediately applies that status filter and refreshes the results.</li>
          <li>The search is case-insensitive for most fields.</li>
          <li>You can clear filters by selecting "All Statuses" or leaving search fields empty.</li>
          <li>Transaction counts in the status cards reflect your current search criteria.</li>
        </ul>
      </div>
    `;
    
    features.push({
      title: 'Dashboard',
      description: 'View overview of transactions and system statistics. Search and filter transaction data.',
      icon: 'dashboard',
      detailedDescription: dashboardDescription
    });

    const uploadDescription = `
      <div style="margin-top: 16px;">
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; font-size: 1rem; font-weight: 600;">Overview</h4>
        <p style="margin-bottom: 16px; color: #495057; line-height: 1.6; font-size: 14px;">
          The Upload Data feature allows you to bulk import transaction records from Excel files. The system automatically validates, processes, and imports the data into the database.
        </p>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Getting Started</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          Before uploading, you can download a template file to ensure your data is in the correct format:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li>Click the <strong>Download Template</strong> button to get an Excel template with the required column structure</li>
          <li>The template includes all required and optional fields with examples</li>
          <li>Use this template as a guide when preparing your data files</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Uploading Files</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          To upload a file:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li>Click the <strong>Choose File</strong> button or drag and drop your Excel file (.xlsx or .xls) into the upload area</li>
          <li>Once selected, the file name will be displayed</li>
          <li>Click the <strong>Upload</strong> button to start the upload process</li>
          <li>The system will show a progress indicator while uploading</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Processing Status</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          After uploading, the system processes your file automatically:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li><strong>Uploaded:</strong> File has been received and is queued for processing</li>
          <li><strong>Processing:</strong> System is validating and importing data</li>
          <li><strong>Completed:</strong> Processing finished successfully</li>
          <li><strong>Failed:</strong> Processing encountered errors (check rejected records)</li>
        </ul>
        <p style="margin-bottom: 16px; color: #495057; line-height: 1.6; font-size: 14px;">
          The system automatically polls for status updates. You'll see real-time progress including:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li><strong>Rows Inserted:</strong> Number of new records added to the database</li>
          <li><strong>Rows Updated:</strong> Number of existing records that were updated</li>
          <li><strong>Rows Rejected:</strong> Number of records that failed validation</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Rejected Records</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          If any records are rejected during processing:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li>Click the <strong>View Rejected Records</strong> button to see details</li>
          <li>Each rejected record shows the row number, data, and reason for rejection</li>
          <li>You can download rejected records as an Excel file to fix and re-upload</li>
          <li>Common rejection reasons include: missing required fields, invalid data formats, a title work that already exists for the same VIN, Request Received Date, and Transaction Type system-wide (for rows without an id), or invalid status values</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Upload History</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          The Upload History section shows all your previous uploads:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li>View file name, upload date, and processing results</li>
          <li>See who uploaded each file</li>
          <li>Check processing status and row counts</li>
          <li>Access rejected records for any upload</li>
          <li>Use pagination to navigate through upload history</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Tips</h4>
        <ul style="margin-bottom: 0; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li>Always use the template file to ensure correct column structure</li>
          <li>Required fields (VIN, Status) must be filled for each row</li>
          <li>VIN must be at least 8 characters long</li>
          <li>Status values must match your agency's configured statuses</li>
          <li>Dates should be in MM/DD/YYYY or YYYY-MM-DD format</li>
          <li>Large files may take several minutes to process - be patient</li>
          <li>You can upload multiple files - each is processed independently</li>
          <li>If processing fails, check rejected records and fix the issues before re-uploading</li>
        </ul>
      </div>
    `;

    if (!isCustomer && !isCustomerAdmin) {
      features.push({
        title: 'Upload Data',
        description: 'Upload Excel files containing transaction data. The system will validate and process the data automatically.',
        icon: 'cloud_upload',
        detailedDescription: uploadDescription
      });
    }

    const reportsDescription = `
      <div style="margin-top: 16px;">
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; font-size: 1rem; font-weight: 600;">Overview</h4>
        <p style="margin-bottom: 16px; color: #495057; line-height: 1.6; font-size: 14px;">
          The Reports feature allows you to generate and download custom reports based on specific criteria. Reports are generated asynchronously and can be downloaded when ready.
        </p>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Available Report Types</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          Currently available report types:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li><strong>Requestor Report:</strong> Generate a report of all transactions for a specific requestor (person or department)</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Generating a Report</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          To generate a Requestor Report:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li>Click the <strong>Generate Requestor Report</strong> button</li>
          <li>Enter the requestor name in the modal dialog</li>
          <li>Click <strong>Generate</strong> to start the report generation process</li>
          <li>The system will create a report job and begin processing</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Report Status</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          Reports go through the following statuses:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li><strong>Pending:</strong> Report job has been created and is waiting to be processed</li>
          <li><strong>Processing:</strong> Report is being generated (data is being collected and formatted)</li>
          <li><strong>Completed:</strong> Report is ready for download</li>
          <li><strong>Failed:</strong> Report generation encountered an error</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Downloading Reports</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          Once a report is completed:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li>Find the report in the "Recent Reports" section</li>
          <li>Click the <strong>Download</strong> button next to the completed report</li>
          <li>The report will be downloaded as an Excel file (.xlsx)</li>
          <li>Reports include all transaction data matching your criteria</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Recent Reports</h4>
        <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
          The Recent Reports section shows:
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li>All reports you have requested, sorted by most recent first</li>
          <li>Report type and parameters used</li>
          <li>Current status and creation date</li>
          <li>Completion date (for completed reports)</li>
          <li>Download button (only available for completed reports)</li>
        </ul>
        
        <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Tips</h4>
        <ul style="margin-bottom: 0; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
          <li>Report generation may take a few minutes for large datasets - be patient</li>
          <li>You can generate multiple reports - each is processed independently</li>
          <li>Reports are stored securely and can be downloaded multiple times</li>
          <li>Only you can download reports you generated</li>
          <li>If a report fails, try generating it again or contact support</li>
          <li>Requestor names are case-sensitive - use the exact name as it appears in transactions</li>
        </ul>
      </div>
    `;

    if (!isCustomer && !isCustomerAdmin) {
      features.push({
        title: 'Reports',
        description: 'Request and download reports. Reports are generated asynchronously and can be downloaded when ready.',
        icon: 'assessment',
        detailedDescription: reportsDescription
      });
    }

    // Features available to Agency Admin and Admin
    if (isAgencyAdmin || isAdmin) {
      const usersDescription = `
        <div style="margin-top: 16px;">
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; font-size: 1rem; font-weight: 600;">Overview</h4>
          <p style="margin-bottom: 16px; color: #495057; line-height: 1.6; font-size: 14px;">
            The Users management page allows you to create, view, update, and manage user accounts within your agency. ${isAdmin ? 'As an Admin, you can manage users across all agencies.' : 'As an Agency Admin, you can only manage users within your agency.'}
          </p>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Viewing Users</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            The users table displays:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Name:</strong> User's full name (First Name + Last Name)</li>
            <li><strong>Email:</strong> User's email address (used for login)</li>
            <li><strong>Agency:</strong> The agency the user belongs to</li>
            <li><strong>Roles:</strong> Assigned roles (e.g., Customer, Agency User, Agency Admin, Admin)</li>
            <li><strong>Status:</strong> Active or Inactive status</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Creating a New User</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            To create a new user:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Click the <strong>Create User</strong> button</li>
            <li>Fill in the required fields:
              <ul style="margin-top: 8px; padding-left: 20px;">
                <li><strong>First Name:</strong> User's first name</li>
                <li><strong>Last Name:</strong> User's last name</li>
                <li><strong>Email:</strong> User's email address (must be unique)</li>
                ${isAdmin ? '<li><strong>Agency:</strong> Select the agency for the user (Admin only)</li>' : ''}
                <li><strong>Role:</strong> Select a role from the dropdown</li>
              </ul>
            </li>
            <li>Click <strong>Create User</strong> to save</li>
            <li>New users are created with Active status by default</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Editing a User</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            To edit an existing user:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Click the <strong>Edit</strong> icon (pencil) in the user's row</li>
            <li>Modify the fields you want to change:
              <ul style="margin-top: 8px; padding-left: 20px;">
                <li><strong>First Name:</strong> Update user's first name</li>
                <li><strong>Last Name:</strong> Update user's last name</li>
                <li><strong>Email:</strong> Update email address (must be unique)</li>
                <li><strong>Role:</strong> Change user's role</li>
                <li><strong>Status:</strong> Toggle between Active and Inactive</li>
              </ul>
            </li>
            <li>Click <strong>Update User</strong> to save changes</li>
            <li>Inactive users cannot log in to the system</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Deleting a User</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            To delete a user:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Click the <strong>Delete</strong> icon (trash) in the user's row</li>
            <li>Confirm the deletion in the modal dialog</li>
            <li>Note: Users with associated transactions or uploads cannot be deleted</li>
            <li>If deletion fails, you may need to reassign or remove associated records first</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">User Roles</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            Available roles and their permissions:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Agency User:</strong> Can view and search transactions, upload data, and generate reports</li>
            <li><strong>Agency Admin:</strong> All Agency User permissions, plus can manage users within their agency</li>
            <li><strong>Admin:</strong> Full system access including agency management, system parameters, and audit trail</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">User Status</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            User status controls access to the system:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Active:</strong> User can log in and access the system</li>
            <li><strong>Inactive:</strong> User cannot log in and will be rejected at login</li>
            <li>Set a user to Inactive to temporarily disable their access without deleting their account</li>
            <li>Inactive users retain their data and can be reactivated later</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Tips</h4>
          <ul style="margin-bottom: 0; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Email addresses must be unique across the entire system</li>
            <li>Users must have at least one role assigned to access the system</li>
            <li>When creating a user, they will need to log in with their email through Azure AD</li>
            <li>${isAdmin ? 'As an Admin, you can manage users from any agency' : 'As an Agency Admin, you can only see and manage users from your agency'}</li>
            <li>Before deleting a user, check if they have associated transactions or uploads</li>
            <li>Consider setting users to Inactive instead of deleting if you want to preserve their history</li>
          </ul>
        </div>
      `;

      features.push({
        title: 'Users',
        description: 'Manage users in your agency. Create, update, and manage user accounts and their roles.',
        icon: 'group',
        detailedDescription: usersDescription
      });
    }

    // Features available only to Admin
    if (isAdmin) {
      const agenciesDescription = `
        <div style="margin-top: 16px;">
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; font-size: 1rem; font-weight: 600;">Overview</h4>
          <p style="margin-bottom: 16px; color: #495057; line-height: 1.6; font-size: 14px;">
            The Agencies management page allows you to create, view, update, and configure agencies in the system. Agencies are organizational units that contain users and transactions.
          </p>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Viewing Agencies</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            The agencies table displays:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Name:</strong> Agency's display name</li>
            <li><strong>Code:</strong> Unique agency code/identifier</li>
            <li><strong>Status:</strong> Active or Inactive status</li>
            <li><strong>Statuses:</strong> Configured transaction statuses for this agency</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Creating a New Agency</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            To create a new agency:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Click the <strong>Create Agency</strong> button</li>
            <li>Fill in the required fields:
              <ul style="margin-top: 8px; padding-left: 20px;">
                <li><strong>Name:</strong> Agency's display name</li>
                <li><strong>Code:</strong> Unique agency code (must be unique across all agencies)</li>
                <li><strong>Statuses:</strong> Comma-separated list of allowed transaction statuses (e.g., "Pending,Processing,Completed")</li>
              </ul>
            </li>
            <li>Click <strong>Create Agency</strong> to save</li>
            <li>New agencies are created with Active status by default</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Editing an Agency</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            To edit an existing agency:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Click the <strong>Edit</strong> icon (pencil) in the agency's row</li>
            <li>Modify the fields you want to change:
              <ul style="margin-top: 8px; padding-left: 20px;">
                <li><strong>Name:</strong> Update agency name</li>
                <li><strong>Code:</strong> Cannot be changed (unique identifier)</li>
                <li><strong>Statuses:</strong> Update allowed transaction statuses</li>
                <li><strong>Status:</strong> Toggle between Active and Inactive</li>
              </ul>
            </li>
            <li>Click <strong>Update Agency</strong> to save changes</li>
            <li>Inactive agencies cannot have new transactions created</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Transaction Statuses</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            Each agency can have custom transaction statuses:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Statuses are configured as a comma-separated list (e.g., "Pending,Processing,Completed,Cancelled")</li>
            <li>These statuses appear in the Dashboard status filter dropdown</li>
            <li>Users can only set transaction statuses that are configured for their agency</li>
            <li>Status values are case-sensitive</li>
            <li>When uploading data, only configured statuses will be accepted</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Agency Status</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            Agency status controls functionality:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Active:</strong> Agency is operational - users can create transactions and upload data</li>
            <li><strong>Inactive:</strong> Agency is disabled - no new transactions can be created</li>
            <li>Existing transactions and data are preserved when an agency is set to Inactive</li>
            <li>Users in inactive agencies can still log in but may have limited functionality</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Tips</h4>
          <ul style="margin-bottom: 0; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Agency codes must be unique and cannot be changed after creation</li>
            <li>Choose status names carefully - they appear throughout the system</li>
            <li>Status names should be clear and descriptive (e.g., "In Progress" instead of "IP")</li>
            <li>You can add or remove statuses at any time by editing the agency</li>
            <li>When updating statuses, ensure existing transactions use valid status values</li>
            <li>Consider agency statuses when planning data uploads</li>
          </ul>
        </div>
      `;

      const systemParametersDescription = `
        <div style="margin-top: 16px;">
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; font-size: 1rem; font-weight: 600;">Overview</h4>
          <p style="margin-bottom: 16px; color: #495057; line-height: 1.6; font-size: 14px;">
            The System Parameters page allows you to configure system-wide settings that control application behavior. These parameters affect how the system operates for all users.
          </p>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Viewing Parameters</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            The parameters table displays:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Key:</strong> Parameter identifier (e.g., "security.bypass")</li>
            <li><strong>Value:</strong> Current parameter value</li>
            <li><strong>Description:</strong> What the parameter controls</li>
            <li><strong>Status:</strong> Active or Inactive</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Common Parameters</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            Some commonly used parameters:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>security.bypass:</strong> When set to "true" and Active, bypasses token validation (development/testing only)</li>
            <li>Other parameters may be added as the system evolves</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Creating a Parameter</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            To create a new parameter:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Click the <strong>Create Parameter</strong> button</li>
            <li>Fill in the required fields:
              <ul style="margin-top: 8px; padding-left: 20px;">
                <li><strong>Key:</strong> Unique parameter key (use dot notation, e.g., "feature.enable_export")</li>
                <li><strong>Value:</strong> Parameter value (can be text, number, or boolean)</li>
                <li><strong>Description:</strong> Brief description of what this parameter controls</li>
              </ul>
            </li>
            <li>Click <strong>Create Parameter</strong> to save</li>
            <li>New parameters are created with Active status by default</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Editing a Parameter</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            To edit an existing parameter:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Click the <strong>Edit</strong> icon (pencil) in the parameter's row</li>
            <li>Modify the fields you want to change:
              <ul style="margin-top: 8px; padding-left: 20px;">
                <li><strong>Value:</strong> Update the parameter value</li>
                <li><strong>Description:</strong> Update the description</li>
                <li><strong>Status:</strong> Toggle between Active and Inactive</li>
              </ul>
            </li>
            <li>Note: Parameter key cannot be changed after creation</li>
            <li>Click <strong>Update Parameter</strong> to save changes</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Parameter Status</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            Parameter status controls whether the parameter is active:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Active:</strong> Parameter is in effect and affects system behavior</li>
            <li><strong>Inactive:</strong> Parameter is disabled and ignored by the system</li>
            <li>Setting a parameter to Inactive effectively disables its functionality</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Important Notes</h4>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Security Parameters:</strong> Be extremely careful when modifying security-related parameters</li>
            <li><strong>Bypass Mode:</strong> The security.bypass parameter should only be used in development/testing environments</li>
            <li><strong>System Impact:</strong> Changes to parameters may affect all users immediately</li>
            <li><strong>Backup:</strong> Consider documenting parameter values before making changes</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Tips</h4>
          <ul style="margin-bottom: 0; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Use descriptive keys with dot notation for organization (e.g., "feature.export.enabled")</li>
            <li>Document what each parameter does in the description field</li>
            <li>Test parameter changes in a development environment first</li>
            <li>Keep security.bypass set to "false" or Inactive in production</li>
            <li>Parameter keys are case-sensitive</li>
            <li>Boolean values should be "true" or "false" (lowercase)</li>
          </ul>
        </div>
      `;

      const auditTrailDescription = `
        <div style="margin-top: 16px;">
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; font-size: 1rem; font-weight: 600;">Overview</h4>
          <p style="margin-bottom: 16px; color: #495057; line-height: 1.6; font-size: 14px;">
            The Audit Trail provides a complete log of all system activities, including user actions, data changes, and system events. This is essential for compliance, security monitoring, and troubleshooting.
          </p>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Viewing Audit Events</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            The audit trail table displays:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Timestamp:</strong> Date and time when the event occurred</li>
            <li><strong>User:</strong> User who performed the action (if applicable)</li>
            <li><strong>Event Type:</strong> Type of event (e.g., LOGIN, TRANSACTION_CREATED, USER_UPDATED)</li>
            <li><strong>Entity Type:</strong> Type of entity affected (e.g., User, Transaction, Agency)</li>
            <li><strong>Entity ID:</strong> ID of the specific entity affected</li>
            <li><strong>Details:</strong> Additional information about the event</li>
            <li><strong>IP Address:</strong> IP address from which the action was performed</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Event Types</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            Common event types tracked:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>LOGIN:</strong> User login attempts (successful and failed)</li>
            <li><strong>TRANSACTION_CREATED:</strong> New transaction records created</li>
            <li><strong>TRANSACTION_UPDATED:</strong> Existing transactions modified</li>
            <li><strong>USER_CREATED:</strong> New user accounts created</li>
            <li><strong>USER_UPDATED:</strong> User account information changed</li>
            <li><strong>USER_DELETED:</strong> User accounts removed</li>
            <li><strong>USER_ROLE_CHANGED:</strong> User role assignments modified</li>
            <li><strong>AGENCY_CREATED:</strong> New agencies created</li>
            <li><strong>AGENCY_UPDATED:</strong> Agency information modified</li>
            <li><strong>UPLOAD_PROCESSED:</strong> Data file uploads processed</li>
            <li><strong>REPORT_GENERATED:</strong> Reports generated and downloaded</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Filtering and Searching</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            You can filter audit events by:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Date Range:</strong> Filter events by date range</li>
            <li><strong>Event Type:</strong> Filter by specific event types</li>
            <li><strong>User:</strong> View events for a specific user</li>
            <li><strong>Entity Type:</strong> Filter by entity type (User, Transaction, etc.)</li>
            <li><strong>IP Address:</strong> Search by IP address</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Event Details</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            Each audit event includes detailed information:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Before/After Values:</strong> For update events, shows what changed</li>
            <li><strong>Action Details:</strong> Specific actions taken (e.g., field names changed)</li>
            <li><strong>Context:</strong> Additional context about the event</li>
            <li><strong>Metadata:</strong> System metadata about the event</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Exporting Audit Data</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            You can export audit trail data:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Use filters to narrow down the events you want to export</li>
            <li>Click the <strong>Export</strong> button to download audit data</li>
            <li>Exported data includes all visible columns and can be used for compliance reporting</li>
            <li>Large date ranges may take time to export</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Use Cases</h4>
          <p style="margin-bottom: 12px; color: #495057; line-height: 1.6; font-size: 14px;">
            The audit trail is useful for:
          </p>
          <ul style="margin-bottom: 16px; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li><strong>Security Monitoring:</strong> Track login attempts and suspicious activities</li>
            <li><strong>Compliance:</strong> Maintain records for regulatory requirements</li>
            <li><strong>Troubleshooting:</strong> Investigate issues by reviewing what actions were taken</li>
            <li><strong>Accountability:</strong> Track who made what changes and when</li>
            <li><strong>Data Integrity:</strong> Verify data changes and maintain change history</li>
          </ul>
          
          <h4 style="color: var(--secondary-color); margin-bottom: 12px; margin-top: 20px; font-size: 1rem; font-weight: 600;">Tips</h4>
          <ul style="margin-bottom: 0; padding-left: 24px; color: #495057; line-height: 1.8; font-size: 14px;">
            <li>Audit events are automatically created - no manual logging required</li>
            <li>Events are stored permanently and cannot be deleted</li>
            <li>Use date range filters to focus on specific time periods</li>
            <li>Export audit data regularly for compliance and backup purposes</li>
            <li>Review failed login attempts regularly for security monitoring</li>
            <li>Check audit trail when investigating data discrepancies</li>
            <li>Large date ranges may take time to load - use filters to narrow results</li>
          </ul>
        </div>
      `;

      features.push({
        title: 'Agencies',
        description: 'Manage agencies in the system. Create, update, and configure agency settings.',
        icon: 'business',
        detailedDescription: agenciesDescription
      });

      features.push({
        title: 'System Parameters',
        description: 'Configure system-wide settings and parameters that control application behavior.',
        icon: 'settings',
        detailedDescription: systemParametersDescription
      });

      features.push({
        title: 'Audit Trail',
        description: 'View complete audit log of all system activities including logins, searches, uploads, and user management actions.',
        icon: 'history',
        detailedDescription: auditTrailDescription
      });
    }

    return features;
  };

  const features = getFeatures();
  const [expandedFeature, setExpandedFeature] = useState<string | null>(null);

  const toggleFeatureDetails = (title: string) => {
    setExpandedFeature(expandedFeature === title ? null : title);
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '32px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ 
            color: 'var(--secondary-color)', 
            marginBottom: '8px',
            fontSize: '2rem',
            fontWeight: '700'
          }}>
            User Guide
          </h1>
          <p style={{ 
            color: '#6c757d', 
            fontSize: '16px',
            margin: 0
          }}>
            Welcome to the Title Tracking Portal. This guide explains the features available to your role.
          </p>
        </div>

        <div style={{ 
          background: '#f8f9fa', 
          borderRadius: '8px', 
          padding: '16px',
          marginBottom: '32px',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            marginBottom: '8px'
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: '20px', color: 'var(--primary-color)' }}>
              person
            </span>
            <span style={{ fontWeight: '600', color: 'var(--secondary-color)' }}>
              Your Role: {getRoleName()}
            </span>
          </div>
          {isBypass && (
            <div style={{ 
              fontSize: '14px', 
              color: '#856404',
              marginTop: '8px',
              padding: '8px',
              background: '#fff3cd',
              borderRadius: '4px',
              border: '1px solid #ffeaa7'
            }}>
              <strong>Note:</strong> You are in bypass mode with full system access.
            </div>
          )}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ 
            color: 'var(--secondary-color)', 
            marginBottom: '16px',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            Available Features
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {features.map((feature, index) => (
              <div 
                key={index}
                style={{ 
                  background: 'white',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '20px',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <span className="material-symbols-outlined" style={{ 
                      fontSize: '28px', 
                      color: 'var(--primary-color)',
                      opacity: 0.8
                    }}>
                      {feature.icon}
                    </span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      color: 'var(--secondary-color)', 
                      margin: '0 0 8px 0',
                      fontSize: '1.25rem',
                      fontWeight: '600'
                    }}>
                      {feature.title}
                    </h3>
                    <p style={{ 
                      color: '#6c757d', 
                      margin: 0,
                      lineHeight: '1.6',
                      fontSize: '15px'
                    }}>
                      {feature.description}
                    </p>
                    {feature.detailedDescription && (
                      <div style={{ marginTop: '12px' }}>
                        <button
                          onClick={() => toggleFeatureDetails(feature.title)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            background: 'none',
                            border: '1px solid var(--primary-color)',
                            color: 'var(--primary-color)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(252, 80, 3, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span className="material-symbols-outlined" style={{ 
                            fontSize: '18px',
                            transform: expandedFeature === feature.title ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}>
                            expand_more
                          </span>
                          {expandedFeature === feature.title ? 'Hide Details' : 'Show Details'}
                        </button>
                        {expandedFeature === feature.title && (
                          <div 
                            style={{ 
                              marginTop: '16px',
                              padding: '16px',
                              background: '#f8f9fa',
                              borderRadius: '8px',
                              border: '1px solid #e9ecef'
                            }}
                            dangerouslySetInnerHTML={{ __html: feature.detailedDescription }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ 
          background: '#e7f3ff', 
          borderRadius: '8px', 
          padding: '20px',
          border: '1px solid #b3d9ff',
          marginTop: '32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span className="material-symbols-outlined" style={{ 
              fontSize: '24px', 
              color: 'var(--primary-color)',
              flexShrink: 0
            }}>
              info
            </span>
            <div>
              <h3 style={{ 
                color: 'var(--secondary-color)', 
                margin: '0 0 8px 0',
                fontSize: '1rem',
                fontWeight: '600'
              }}>
                Need Help?
              </h3>
              <p style={{ 
                color: '#495057', 
                margin: 0,
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                If you need assistance with any feature or have questions about your access, please contact your system administrator.
              </p>
            </div>
          </div>
        </div>

        <div style={{ 
          background: '#fff3cd', 
          borderRadius: '8px', 
          padding: '20px',
          border: '1px solid #ffeaa7',
          marginTop: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span className="material-symbols-outlined" style={{ 
              fontSize: '24px', 
              color: '#856404',
              flexShrink: 0
            }}>
              lock_reset
            </span>
            <div>
              <h3 style={{ 
                color: '#856404', 
                margin: '0 0 8px 0',
                fontSize: '1rem',
                fontWeight: '600'
              }}>
                Forgot Your Password?
              </h3>
              <p style={{ 
                color: '#856404', 
                margin: '0 0 12px 0',
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                If you've forgotten your password, you can reset it using Azure AD's self-service password reset:
              </p>
              <ol style={{ 
                color: '#856404', 
                margin: '0 0 12px 0',
                paddingLeft: '20px',
                fontSize: '14px',
                lineHeight: '1.8'
              }}>
                <li>Go to the login page and click <strong>"Forgot your password?"</strong></li>
                <li>Enter your email address when prompted</li>
                <li>Follow the instructions sent to your email</li>
                <li>Create a new password</li>
                <li>Return to the login page and sign in with your new password</li>
              </ol>
              <p style={{ 
                color: '#856404', 
                margin: 0,
                fontSize: '13px',
                lineHeight: '1.6',
                fontStyle: 'italic'
              }}>
                <strong>Note:</strong> Password reset is managed by your organization's Azure AD. If you need assistance, contact your IT administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserGuide;

